import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { CreateCaseForm } from '@/types';
import { CasesApi } from '@/api';
import { RiArrowLeftLine as ArrowLeftIcon } from 'react-icons/ri';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import { usePageTracking } from '@/hooks/usePageTracking';

// Helper to normalize whitespace: trim ends and collapse internal multiple spaces
const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();

const NewCasePage: React.FC = () => {
  usePageTracking(); // Track page view
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateCaseForm>({
    name: '',
    description: '',
    visaType: '',
    applicantName: '',
    petitionerCompanyName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [visaTypes, setVisaTypes] = useState<string[]>([]);
  const [visaTypesLoading, setVisaTypesLoading] = useState<boolean>(false);
  const [visaTypesError, setVisaTypesError] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showDescription, setShowDescription] = useState(false);
  const [isSelfPetitioner, setIsSelfPetitioner] = useState(true);
  const [formStartTime] = useState(Date.now()); // Track form start time

  useEffect(() => {
    let mounted = true;
    setVisaTypesLoading(true);
    CasesApi.fetchVisaTypes()
      .then(list => {
        if (mounted) setVisaTypes(list);
      })
      .catch(() => {
        if (mounted) setVisaTypesError('Failed to load visa types');
      })
      .finally(() => {
        if (mounted) setVisaTypesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Auto-generate applicantName and case name (with normalization)
  useEffect(() => {
    const applicant = [normalize(firstName), normalize(lastName)]
      .filter(Boolean)
      .join(' ');
    const visaPart = formData.visaType
      ? ` // ${normalize(formData.visaType)}`
      : '';
    const generatedCaseName = applicant
      ? `${applicant}${visaPart}`
      : formData.visaType
        ? `Case${visaPart}`
        : '';
    setFormData(prev => ({
      ...prev,
      applicantName: applicant,
      name: generatedCaseName,
    }));
  }, [firstName, lastName, formData.visaType]);

  // Reset self-petitioner checkbox when visa type changes
  // O-1A: Always employer-sponsored (NOT self-petition)
  // EB-1A: Defaults to self-petition but can be employer-sponsored
  useEffect(() => {
    if (formData.visaType === 'EB-1A') {
      setIsSelfPetitioner(true);
    } else if (formData.visaType === 'O-1A') {
      setIsSelfPetitioner(false); // O-1A is always employer-sponsored
    }
  }, [formData.visaType]);

  // Keep spinner until navigation completes; enforce minimal visual duration
  const minSpinnerMs = 500; // minimal time to show spinner
  const submitStartRef = useRef<number | null>(null);

  useEffect(() => {
    const handleRouteChange = () => {
      // When route changes (navigating away), we can allow spinner to stop
      setIsLoading(false);
    };
    // Not needed with react-router-dom; navigation is instantaneous.
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    submitStartRef.current = performance.now();

    // Prepare sanitized payload (declare before try for catch block access)
    const payload: CreateCaseForm = {
      name: normalize(formData.name || ''),
      applicantName: normalize(formData.applicantName || ''),
      visaType: normalize(formData.visaType || ''),
      description: formData.description ? normalize(formData.description) : '',
      petitionerCompanyName: formData.petitionerCompanyName
        ? normalize(formData.petitionerCompanyName)
        : undefined,
    };

    try {
      const response = await CasesApi.createCase(payload);
      console.log('Create case response:', response);

      // Handle the response format from API: { success: true, case: { id: "...", ... }, message: "..." }
      const caseId = response.case?.id;

      if (caseId) {
        // Track successful case creation
        const creationDuration = Date.now() - formStartTime;
        analytics.track(ANALYTICS_EVENTS.CASE_CREATED, {
          visa_type: payload.visaType,
          has_description: !!payload.description,
          has_company: !!payload.petitionerCompanyName,
          is_self_petitioner: isSelfPetitioner,
          creation_duration_ms: creationDuration,
        });

        // Ensure spinner shows at least minSpinnerMs before navigating
        const elapsed = submitStartRef.current
          ? performance.now() - submitStartRef.current
          : 0;
        const remaining = Math.max(0, minSpinnerMs - elapsed);
        setTimeout(() => {
          navigate(`/cases/${caseId}`);
        }, remaining);
      } else {
        console.error('No case ID found in response:', response);
        throw new Error('No case ID returned from API');
      }
    } catch (err) {
      // Track case creation failure
      analytics.track(ANALYTICS_EVENTS.CASE_CREATION_FAILED, {
        visa_type: payload.visaType,
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });

      setError('Failed to create case');
      console.error('Create case error:', err);
    } finally {
      // We stop loading state on route change events; if navigate didn't happen, fallback with min delay
      const elapsed = submitStartRef.current
        ? performance.now() - submitStartRef.current
        : 0;
      const remaining = Math.max(0, minSpinnerMs - elapsed);
      setTimeout(() => {
        setIsLoading(false);
      }, remaining);
    }
  };

  const handleInputChange =
    (field: keyof CreateCaseForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  return (
    <>
      <Helmet>
        <title>New Case - ImmStack</title>
      </Helmet>
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <Link to="/cases">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center space-x-1"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span>Back to Cases</span>
              </Button>
            </Link>
          </div>

          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">
              Create New Case
            </h1>
            <p className="text-neutral-600 mt-1">
              Set up a new immigration case to organize your documents and
              workflow
            </p>
          </div>

          <Card padding="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                  <p className="text-sm text-error-700">{error}</p>
                </div>
              )}

              {/* Identity & Visa row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Input
                    label="First Name *"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    fullWidth
                    placeholder="John"
                  />
                </div>
                <div>
                  <Input
                    label="Last Name *"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    fullWidth
                    placeholder="Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Visa Type *
                  </label>
                  <div className="relative">
                    <select
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      value={formData.visaType}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          visaType: e.target.value,
                        }))
                      }
                      required
                      disabled={visaTypesLoading}
                    >
                      <option value="">
                        {visaTypesLoading ? 'Loading...' : 'Select visa type'}
                      </option>
                      {visaTypes.map(v => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    {visaTypesError ? visaTypesError : ''}
                  </p>
                </div>
              </div>

              {/* O-1A: Always requires company petitioner (employer-sponsored) */}
              {formData.visaType === 'O-1A' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>O-1A Visa Requirements:</strong> O-1A is an
                      employer-sponsored temporary work visa. A U.S. company or
                      agent must petition on behalf of {normalize(firstName)}{' '}
                      {normalize(lastName)}.
                    </p>
                  </div>

                  <div>
                    <Input
                      label="Petitioner Company Name *"
                      value={formData.petitionerCompanyName || ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          petitionerCompanyName: e.target.value,
                        }))
                      }
                      required
                      fullWidth
                      placeholder="e.g., Tech Innovations Inc."
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      The U.S. company or agent filing the O-1A petition on
                      behalf of {normalize(firstName)} {normalize(lastName)}.
                    </p>
                  </div>
                </div>
              )}

              {/* Self-Petitioner checkbox for EB-1A */}
              {formData.visaType === 'EB-1A' && (
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelfPetitioner}
                      onChange={e => setIsSelfPetitioner(e.target.checked)}
                      className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-neutral-700">
                        Self-Petitioner
                      </span>
                      <span className="text-xs text-neutral-500">
                        {normalize(firstName)} {normalize(lastName)} is filing
                        the petition themselves (no sponsor required)
                      </span>
                    </div>
                  </label>

                  {/* Petitioner Name field - shown when NOT self-petitioner */}
                  {!isSelfPetitioner && (
                    <div>
                      <Input
                        label="Petitioner Name *"
                        value={formData.petitionerCompanyName || ''}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            petitionerCompanyName: e.target.value,
                          }))
                        }
                        required
                        fullWidth
                        placeholder="e.g., Tech Innovations Inc. or Agent Name"
                      />
                      <p className="mt-1 text-xs text-neutral-500">
                        Name of the company or agent filing on behalf of the
                        beneficiary.
                      </p>
                    </div>
                  )}

                  {/* Info message when self-petitioner */}
                  {isSelfPetitioner && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs text-gray-700">
                        <strong>EB-1A Self-Petition:</strong>{' '}
                        {normalize(firstName)} {normalize(lastName)} is both the
                        petitioner and beneficiary. No company sponsor is
                        required.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Auto-generated case name preview */}
              {/* Case Name (auto) preview removed per simplification request */}

              {/* Optional description toggle */}
              <div>
                {!showDescription && (
                  <button
                    type="button"
                    className="text-sm text-primary-600 hover:text-primary-700"
                    onClick={() => setShowDescription(true)}
                  >
                    + Add optional description
                  </button>
                )}
                {showDescription && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Description{' '}
                      <span className="text-neutral-400">(optional)</span>
                    </label>
                    <textarea
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange('description')}
                      placeholder="Brief description of the case..."
                    />
                    <button
                      type="button"
                      className="mt-1 text-xs text-neutral-500 hover:text-neutral-700"
                      onClick={() => setShowDescription(false)}
                    >
                      Hide description
                    </button>
                  </div>
                )}
              </div>

              {/* Removed secondary grid (visa + applicant preview) after consolidation */}

              <div className="flex justify-end space-x-3 pt-4">
                <Link to="/cases">
                  <Button variant="secondary">Cancel</Button>
                </Link>

                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={
                    !normalize(firstName) ||
                    !normalize(lastName) ||
                    !normalize(formData.visaType || '') ||
                    (formData.visaType === 'O-1A' &&
                      !normalize(formData.petitionerCompanyName || '')) ||
                    (formData.visaType === 'EB-1A' &&
                      !isSelfPetitioner &&
                      !normalize(formData.petitionerCompanyName || '')) ||
                    isLoading
                  }
                >
                  Create Case
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </Layout>
    </>
  );
};

export default NewCasePage;
